import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight, Share2 } from 'lucide-react';
import { motion } from 'framer-motion';

const EventCard = ({ event, index = 0 }) => {
  const navigate = useNavigate();
  const imageUrl = typeof event.image_url === 'string' ? event.image_url.trim() : '';
  const useDefaultImage = !imageUrl || imageUrl.includes('images.unsplash.com');
  const cardImage = useDefaultImage ? '/default-event.jpg' : imageUrl;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      year: date.getFullYear()
    };
  };

  const formatEventDateTime = () => {
    const dateText = new Date(event.event_date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    return event.event_time ? `${dateText} at ${event.event_time}` : dateText;
  };

  const buildSharePayload = () => {
    const shareUrl = event.ticket_url || (typeof window !== 'undefined' ? `${window.location.origin}/events` : 'https://whatshappeningbaltimore.com/events');
    const venueLine = [event.venue_name, event.city].filter(Boolean).join(', ');
    const shareText = [
      event.title,
      formatEventDateTime(),
      venueLine,
      event.description,
    ].filter(Boolean).join('\n\n');

    return {
      title: event.title,
      text: shareText,
      url: shareUrl,
    };
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const payload = buildSharePayload();
    if (navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    const fallbackText = `${payload.text}\n\n${payload.url}`;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(fallbackText);
      alert('Event share details copied to clipboard.');
    } else {
      window.location.href = `mailto:?subject=${encodeURIComponent(payload.title)}&body=${encodeURIComponent(fallbackText)}`;
    }
  };

  const dateInfo = formatDate(event.event_date);
  const detailPath = `/events/${event.id}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`event-card-${event.id}`}
      role="link"
      tabIndex={0}
      onClick={() => navigate(detailPath)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(detailPath);
        }
      }}
      className="event-card bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden group hover:-translate-y-1 hover:border-purple-500/50 transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500"
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={cardImage}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent" />
        
        {/* Date Badge */}
        <div className="absolute top-4 left-4 bg-purple-600 text-white px-3 py-2 rounded-lg text-center">
          <p className="text-xl font-bold leading-none">{dateInfo.day}</p>
          <p className="text-xs uppercase tracking-wide">{dateInfo.month}</p>
        </div>

        {/* Featured Badge */}
        {event.is_featured && (
          <div className="absolute top-4 right-4 bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-semibold">
            Featured
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-outfit text-lg font-semibold text-white mb-2 line-clamp-2 group-hover:text-purple-400 transition-colors">
          {event.title}
        </h3>

        <div className="space-y-1.5 mb-3">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Calendar size={14} className="text-purple-500" />
            <span>
              {new Date(event.event_date).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
              })}
              {event.event_time && ` at ${event.event_time}`}
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <MapPin size={14} className="text-purple-500" />
            <span className="line-clamp-1">{event.venue_name}, {event.city}</span>
          </div>
        </div>

        <p className="text-gray-500 text-sm line-clamp-2 mb-4">
          {event.description}
        </p>

        <div className="flex items-center justify-between gap-3">
          {event.ticket_url ? (
            <a
              href={event.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`event-ticket-${event.id}`}
              className="inline-flex items-center gap-2 text-purple-500 hover:text-purple-400 font-semibold text-sm transition-colors"
            >
              Get Tickets
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </a>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(detailPath);
              }}
              className="inline-flex items-center gap-2 text-purple-500 hover:text-purple-400 font-semibold text-sm transition-colors"
            >
              Find out more
              <ArrowRight size={14} />
            </button>
          )}

          <button
            type="button"
            onClick={handleShare}
            data-testid={`event-share-${event.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-1.5 text-xs font-semibold text-gray-300 hover:border-purple-500 hover:text-purple-400 transition-colors"
            aria-label={`Share ${event.title}`}
          >
            <Share2 size={13} />
            Share
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default EventCard;
